import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import { updateLocationResult } from '@alltrails/explore-service/redux/exploreServiceSlice';
import type BoundingBox from '@alltrails/explore-service/types/BoundingBox';
import getMapBoundingBox from '@alltrails/explore-service/utils/getMapBoundingBox';
import {
  activityParamKey,
  bottomRightLatParamKey,
  bottomRightLngParamKey,
  difficultyParamKey,
  distanceAwayParamKey,
  durationParamKey,
  elevationGainParamKey,
  featureParamKey,
  highestPointParamKey,
  lengthParamKey,
  minRatingParamKey,
  routeTypeParamKey,
  sortParamKey,
  suitabilityParamKey,
  topLeftLatParamKey,
  topLeftLngParamKey,
  trailEngagementParamKey,
  trailTrafficParamKey
} from '@alltrails/filters/utils/exploreSearchParams';
import useMap from '@alltrails/maps/hooks/useMap';
import type { AllTrailsBounds } from '@alltrails/maps/types/Geo';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import { explorePath } from '@/utils/constants';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setIsReturningToExplore } from '@/utils/slices/exploreSlice';

const boundingBoxParamKeys = [bottomRightLatParamKey, bottomRightLngParamKey, topLeftLatParamKey, topLeftLngParamKey] as const;

const filterParamKeys = [
  activityParamKey,
  difficultyParamKey,
  distanceAwayParamKey,
  durationParamKey,
  elevationGainParamKey,
  featureParamKey,
  highestPointParamKey,
  lengthParamKey,
  minRatingParamKey,
  routeTypeParamKey,
  sortParamKey,
  suitabilityParamKey,
  trailEngagementParamKey,
  trailTrafficParamKey
] as const;

type UseNavigateBackOptions = {
  /**
   * If true, skip `router.back()` and instead navigate forward to `/explore` with the current map viewport's
   * bounds. Active filter and sort params from the current URL are preserved unless a `clear*` option strips
   * them explicitly.
   */
  preserveCurrentBounds?: boolean;
  /** Strip filter/sort/distance-away/trail-engagement params from the destination URL. */
  clearFilters?: boolean;
};

function getBoundsFromViewport(viewport: BoundingBox): AllTrailsBounds | undefined {
  const { topLeft, bottomRight } = viewport;

  // getMapBoundingBox returns all-zero corners when map.getBounds() is not ready yet.
  if (topLeft.lng === 0 && topLeft.lat === 0 && bottomRight.lng === 0 && bottomRight.lat === 0) {
    return undefined;
  }

  return {
    longitudeTopLeft: topLeft.lng,
    latitudeTopLeft: topLeft.lat,
    longitudeBottomRight: bottomRight.lng,
    latitudeBottomRight: bottomRight.lat
  };
}

function buildExploreUrl(
  bounds: AllTrailsBounds | undefined,
  languageRegionCode: LanguageRegionCode,
  preservedSearchParams?: URLSearchParams,
  { clearFilters = false }: UseNavigateBackOptions = {}
) {
  const searchParams = new URLSearchParams(preservedSearchParams?.toString() ?? '');

  boundingBoxParamKeys.forEach(key => {
    searchParams.delete(key);
  });
  if (clearFilters) {
    filterParamKeys.forEach(key => {
      searchParams.delete(key);
    });
  }

  if (bounds) {
    searchParams.set(bottomRightLatParamKey, String(bounds.latitudeBottomRight));
    searchParams.set(bottomRightLngParamKey, String(bounds.longitudeBottomRight));
    searchParams.set(topLeftLatParamKey, String(bounds.latitudeTopLeft));
    searchParams.set(topLeftLngParamKey, String(bounds.longitudeTopLeft));
  }

  const queryString = decodeURIComponent(searchParams.toString());
  const baseUrl = wrapUrlSafe(explorePath, languageRegionCode);

  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/**
 * Returns to `/explore`, with two modes depending on whether the caller wants to pan the map back.
 *
 * Default (pan back) — used by sidebar back arrows. If the user got to the sub-page via client-side navigation,
 * calls `router.back()` so the browser restores the previous URL's bounds; on a direct landing, pushes to
 * `/explore` with the stored `returningBounds`.
 *
 * @param {string} [fallbackUrl] - override the direct-landing destination — e.g. a recording
 * detail page sends the user to the owner's recordings list instead of explore root.
 * The browser-history (`router.back()`) path is unaffected.
 *
 * @param {UseNavigateBackOptions} [options] - `preserveCurrentBounds` picks the current viewport instead of
 * relying on `router.back()`. `clearFilters` opts into URL-strip behavior; when the current URL actually
 * contains filter params, the hook uses `router.push` with a stripped URL.
 */
export function useNavigateBack(fallbackUrl?: string, options: UseNavigateBackOptions = {}) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const searchParams = useSearchParams();
  const map = useMap();
  const languageRegionCode = useLanguageRegionCode();
  const { hasNavigatedClientSide, returningBounds } = useAppSelector(state => ({
    hasNavigatedClientSide: state.explore.hasNavigatedClientSide,
    returningBounds: state.explore.returningBounds
  }));

  const navigateBack = useCallback(() => {
    const urlHasFilters = options.clearFilters && filterParamKeys.some(key => searchParams?.has(key));

    if (options.preserveCurrentBounds || urlHasFilters) {
      dispatch(updateLocationResult(undefined));
      const preservedSearchParams = new URLSearchParams(searchParams?.toString() ?? '');
      let bounds: AllTrailsBounds | undefined;
      if (options.preserveCurrentBounds) {
        const viewport = map ? getMapBoundingBox(map) : undefined;
        bounds = viewport ? getBoundsFromViewport(viewport) : undefined;
      } else {
        bounds = returningBounds;
        dispatch(setIsReturningToExplore(true));
      }
      router.push(buildExploreUrl(bounds, languageRegionCode, preservedSearchParams, { clearFilters: urlHasFilters }));
      return;
    }

    if (hasNavigatedClientSide) {
      dispatch(updateLocationResult(undefined));
      dispatch(setIsReturningToExplore(true));
      router.back();
      return;
    }

    // Direct landing with an explicit fallback — go straight there (no explore-restore state).
    if (fallbackUrl) {
      router.push(wrapUrlSafe(fallbackUrl, languageRegionCode));
      return;
    }

    // Direct landing — navigate to explore root with stored bounds
    dispatch(updateLocationResult(undefined));
    dispatch(setIsReturningToExplore(true));
    if (returningBounds) {
      router.push(buildExploreUrl(returningBounds, languageRegionCode));
    } else {
      router.push(wrapUrlSafe(explorePath, languageRegionCode));
    }
  }, [
    dispatch,
    fallbackUrl,
    hasNavigatedClientSide,
    languageRegionCode,
    map,
    options.clearFilters,
    options.preserveCurrentBounds,
    returningBounds,
    router,
    searchParams
  ]);

  return navigateBack;
}
